import { FC, useCallback, useLayoutEffect, useRef, useState } from "react";
import { Drawer, Grid, Stack, Typography } from "@mui/material";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import { useFlag } from "@unleash/proxy-client-react";
import { BillingSummaryCard } from "./cards/BillingSummaryCard";
import { BillingExplorerWidgetCard } from "./cards/BillingExplorerWidgetCard";
import { CostAnomaliesCard } from "./cards/CostAnomaliesCard";
import { CommitmentUtilizationWidgetCard } from "./cards/CommitmentUtilizationWidgetCard";
import { TextWidgetCard } from "./cards/TextWidgetCard";
import { CoverageWidgetCard } from "./cards/CoverageWidgetCard";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { widgetSelectorStateSelector } from "@/store/dashboards/selectors/dashboard/widgetSelectorStateSelector";
import { setWidgetSelectorState } from "@/store/dashboards/dashboardsSlice";
import { NormalizedCostWidgetCard } from "@/components/pages/dashboard/components/adding-widget/widget-select/cards/NormalizedCostWidgetCard";
import { usePanelHorizontalResize } from "@/components/utils/hooks/usePanelHorizontalResize";
import { ResourceDetailsDragHandler } from "@/components/pages/common/resource-details/components/ResourceDetailsDragHandler";

export const WidgetSelectDrawer: FC = () => {
  const ref = useRef<null | HTMLDivElement>(null);
  const { width, handleMouseDown } = usePanelHorizontalResize(
    resizingDefaultParams,
  );
  const dispatch = useAppDispatch();
  const widgetSelectorOpen = useAppSelector(widgetSelectorStateSelector);

  const [sm, setSm] = useState(6);

  const enableCoverageWidget = useFlag("EnableCoverageWidget");
  const enableNormalizedCostWidget = useFlag("EnableNormalizedCostWidget");

  const closeWidgetSelector = useCallback(() => {
    dispatch(setWidgetSelectorState(false));
  }, [dispatch]);

  useLayoutEffect(() => {
    setTimeout(() => {
      const width = ref.current?.offsetWidth ?? minWidth;
      setSm(width > breakPoint ? 4 : 6);
    }, 0);
  }, [widgetSelectorOpen, width]);

  return (
    <Drawer
      anchor="right"
      open={widgetSelectorOpen}
      onClose={closeWidgetSelector}
      PaperProps={{ sx: { width, minWidth, maxWidth } }}
    >
      <ResourceDetailsDragHandler onMouseDown={handleMouseDown} />

      <Stack ref={ref} width="100%" px={2} pb={2}>
        <Stack
          py={2}
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          top={0}
          bgcolor="white"
          zIndex="appBar"
          position="sticky"
        >
          <Typography variant="subtitle1" fontWeight="medium">
            Add Widget
          </Typography>

          <IconButton onClick={closeWidgetSelector} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>

        <Grid container spacing={2}>
          <Grid item sm={sm}>
            <BillingSummaryCard />
          </Grid>

          <Grid item sm={sm}>
            <BillingExplorerWidgetCard />
          </Grid>

          <Grid item sm={sm}>
            <CostAnomaliesCard />
          </Grid>

          <Grid item sm={sm}>
            <TextWidgetCard />
          </Grid>

          <Grid item sm={sm}>
            <CommitmentUtilizationWidgetCard />
          </Grid>

          {enableCoverageWidget && (
            <Grid item sm={sm}>
              <CoverageWidgetCard />
            </Grid>
          )}

          {enableNormalizedCostWidget && (
            <Grid item sm={sm}>
              <NormalizedCostWidgetCard />
            </Grid>
          )}
        </Grid>
      </Stack>
    </Drawer>
  );
};

const resizingDefaultParams = {
  defaultWidthPercent: 45,
  minWidthPercent: 30,
  key: "SelectWidgetDrawer",
};
const minWidth = 736;
const maxWidth = 1104;
const breakPoint = 790;
